/**
 * THE ONLY FILE IN THE REPOSITORY THAT IMPORTS `lucide-react`.
 *
 * Research D-030 makes this a rule with teeth rather than a comment: an ESLint
 * `no-restricted-imports` rule forbids the library everywhere under
 * `apps/web/src/**` and `apps/web/dev/**` and exempts this path alone, so a
 * direct import fails `pnpm lint`.
 *
 * Two things live here and nothing else:
 *
 *   1. the import of the glyphs Cavaquinho actually uses, and
 *   2. `ICON_REGISTRY`, the curated map from a Cavaquinho icon name to one of
 *      them, from which `IconName` is derived.
 *
 * The Lucide catalogue never crosses this boundary. There is no re-export of
 * the library, no `import * as icons`, no lookup by an arbitrary string, and
 * `LucideIcon` appears in no public signature — callers name an icon, they
 * never hold a glyph. That is what makes the set that ships legible (design
 * system § 10 asks for a small consistent set) and what makes replacing Lucide
 * — Tabler is D-022's named fallback — an edit to this file and the map below.
 *
 * ADDING AN ICON: add the import and one entry. That deliberate step is the
 * point; the registry is a curated set, not a window onto the catalogue.
 *
 * NOT icons, and never entries here: the waveform, the rings and the ambient
 * blobs are the design system's own SVG and CSS motifs (§ 10) and stay out of
 * the accessibility tree (§ 12); note names are typography, never a glyph.
 */
import {
  AlarmClock,
  ArrowDown,
  ArrowLeft,
  ArrowUp,
  Bell,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  CircleAlert,
  Compass,
  Download,
  Flame,
  Gamepad2,
  Headphones,
  LogOut,
  Play,
  Plus,
  RotateCcw,
  Search,
  ShieldCheck,
  Trash2,
  TrendingUp,
  Trophy,
  Upload,
  UserRound,
  X,
  type LucideIcon,
} from 'lucide-react';

/**
 * The curated set. Names are Cavaquinho's own — kebab-case, describing the
 * glyph rather than the occasion it is used for, so one entry serves every
 * call site without the map growing a synonym per feature.
 */
export const ICON_REGISTRY = {
  /** Result disc on a correct answer; the completed state of a list item. */
  check: Check,
  /** Result disc on a miss. § 14: never a red flash, never a shake. */
  close: X,
  /** The field-level error message of § 14's Input error state. */
  'alert-circle': CircleAlert,
  /** Replay, on the listening control. Replays are never counted (FR-067). */
  'rotate-ccw': RotateCcw,
  /** Back, in § 14's task bar. */
  'arrow-left': ArrowLeft,
  /** Play, on the listening control and in the gallery's icon examples. */
  play: Play,
  /** Destructive actions in the administrative surface. */
  trash: Trash2,
  /** Notifications — gallery only today; kept because § 14 documents the shape. */
  bell: Bell,
  /** The Select control's own affordance (§ 14 Select). */
  'chevron-down': ChevronDown,
  /**
   * Taking a copy of everything the platform holds, on the account screen
   * (FR-072). An arrow into a tray: the file lands on the learner's machine,
   * which is the one thing the label "Baixar" has to be believed about.
   */
  download: Download,
  /**
   * Leaving the session, on the account screen. An arrow out of a door, not
   * `close`: ending a session is a departure, and the X is already spoken for
   * as "this was wrong" on a result disc.
   */
  'log-out': LogOut,

  /*
   * The administrative surface (§ 16 density). Every entry below is named by
   * the glyph rather than by the occasion, per the rule above — `arrow-up`
   * serves both a table's sort indicator and a list's move control, and the
   * map does not grow a synonym for each.
   */

  /** Reordering a list, and the sorted direction of a § 14 Data table column. */
  'arrow-up': ArrowUp,
  'arrow-down': ArrowDown,
  /** Previous and next, in § 14 Pagination. */
  'chevron-left': ChevronLeft,
  'chevron-right': ChevronRight,
  /** The search field of a § 14 Toolbar. */
  search: Search,
  /** An action that adds a record — a new Lesson, a new Exercise. */
  plus: Plus,
  /** A person, on a § 14 Stat tile counting accounts. */
  user: UserRound,
  /** An administrator, on the tile counting them. Role, not achievement. */
  shield: ShieldCheck,
  /** The Dropzone's own icon — a file field, drawn rather than left bare. */
  upload: Upload,

  /*
   * The landing page's feature grid (features/account/SignIn.tsx). Each names
   * the benefit it sits beside rather than the occasion, per the rule above.
   */

  /** Ear training, listening-first practice. */
  headphones: Headphones,
  /** A guided path through content. */
  compass: Compass,
  /** Short sessions. */
  'alarm-clock': AlarmClock,
  /** Progress over time. */
  'trending-up': TrendingUp,
  /** A practice streak. */
  flame: Flame,
  /**
   * A week finished in the top three, on the profile.
   *
   * `shield` stood here before, and it is the wrong glyph twice over: the
   * entry above reserves it for a ROLE an operator granted, and a shield says
   * "protected" where the figure beside it means "won". A trophy is the thing
   * itself. It is not a MEDAL — § 2 bans the gold/silver/bronze palette, and
   * this is a single outline at the text's own colour, which is a count's mark
   * and not a podium in miniature.
   */
  trophy: Trophy,
  /** An interactive exercise. */
  gamepad: Gamepad2,
} as const satisfies Record<string, LucideIcon>;

/**
 * The public parameter type: a closed union of the registry's own keys.
 *
 * Deliberately not `keyof typeof ICON_REGISTRY | string` and deliberately not
 * `LucideIcon` — a union of literal keys is what keeps the set closed, keeps an
 * unknown name a compile error rather than a runtime blank, and keeps the
 * bundle tree-shakeable, since every glyph is still a named static import.
 */
export type IconName = keyof typeof ICON_REGISTRY;
